<template>
  <div v-if="showAlert"
      :class="['alert-modal', {'alert-modal--dark': darkBackground}]">
    <div class="alert-modal__container">
      <div class="alert-modal__header">
        <div class="alert-modal__icon">
          <p4a-icon :icon="icon"/>
        </div>
        <div class="alert-modal__title">
          <h1>{{title}}</h1>
        </div>
      </div>
      <div class="alert-modal__content">
        <div class="alert-modal__content-icon"
             v-if="showWarningIcon">
          <p4a-icon :icon="'state-warning'"
                    style="color: #EF9825;" />
        </div>
        <div class="alert-modal__content-text">
          <div class="alert-modal__content-title">
            <h2>{{maxSizeSubstring(messageHeader)}}</h2>
          </div>
          <div class="alert-modal__content-text-lines" v-if="hasLines">
            {{firstLine}}
            <br v-if="secondLine"/>
            {{secondLine}}
          </div>
        </div>
      </div>
      <div class="alert-modal__footer">
        <p4a-button class="alert-modal__footer__approve alert-modal__footer__button"
          :showIcon="false"
          :size="'small'"
          :disable="false"
          :product="product"
          :text="confirmationText"
          @click="$emit('on-close', true)" />
        <p4a-button v-if="cancellationText"
          class="alert-modal__footer__cancel alert-modal__footer__button"
          :showIcon="false"
          :size="'small'"
          :disable="false"
          :text="cancellationText"
          @click="$emit('on-close', false)" />
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    props: {
      showAlert: {
        type: Boolean,
        required: true
      },
      darkBackground: {
        type: Boolean,
        default: true
      },
      title: {
        type: String,
        required: true
      },
      icon: {
        type: String,
        required: true
      },
      showWarningIcon: {
        type: Boolean,
        default: false
      },
      messageHeader: {
        type: String,
        required: true
      },
      messageLineOne: {
        type: String,
        default: ''
      },
      messageLineTwo: {
        type: String,
        default: ''
      },
      confirmationText: {
        type: String,
        required: true
      },
      cancellationText: {
        type: String,
        required: false,
        default: ''
      },
      product: String
    },
    data () {
      return {
      }
    },
    computed: {
      hasLines () {
        return this.messageLineOne || this.messageLineTwo;
      },
      hasTwoLines () {
        return this.messageLineOne && this.messageLineTwo;
      },
      firstLine () {
        let result = '';

        if (this.messageLineOne) {
          result = this.messageLineOne;
        } else if (this.messageLineTwo) {
          result = this.messageLineTwo;
        }

        if (result.length > 50) {
          result = this.maxSizeSubstring(result);
        }

        return result;
      },
      secondLine () {
        let result = '';
        let leftOver = '';

        if (this.messageLineOne) {
          leftOver = this.messageLineOne;
        } else if (this.messageLineTwo && this.messageLineTwo > 50) {
          leftOver = this.messageLineTwo;
        }

        if (leftOver.length > 50) {
          result = this.maxSizeSubstring(leftOver, true);
        } else {
          result = '';
          leftOver = '';
        }

        if (leftOver && this.hasTwoLines)
        {
          result += ' ';
        }

        if (this.hasTwoLines) {
          result += this.messageLineTwo;
        }

        return result;
      }
    },
    methods: {
      maxSizeSubstring(input, after) {
        if (input.length <  50) {
          return input;
        }

        let breakPoint = 0;
        while (input.indexOf(' ', breakPoint+1) < 50 &&
              input.indexOf(' ', breakPoint+1) > -1) {
          breakPoint = input.indexOf(' ', breakPoint+1);
        }

        if (breakPoint > 0) {
          if (after !== undefined && after) {
            input = input.substring(breakPoint + 1);
          } else {
            input = input.substring(0, breakPoint);
          }
        }

        return input;
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../styling/styling.scss';

  .alert-modal {
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    z-index: 401;
    display: flex;
    position: fixed;
    align-items: center;
    justify-content: center;

    &--dark { background-color: rgba(0,0,0,0.1); }

    &__container {
      width: rem(415);
      height: rem(210);
      display: flex;
      position: relative;
      border-radius: rem(2);
      flex-direction: column;
      background-color: $white;
      box-shadow: 0 0 rem(25) 0 rgba(0,0,0,.2);
    }

    &__header {
      height: rem(50);
      align-items: center;

      .alert-modal__icon {
        width: rem(50);
        height: rem(50);
        float: left;
        padding: rem(16);
        color: $icon-color;

        .p4a-icon { font-size: rem(18); }
      }

      .alert-modal__title h1 {
        padding-top: 0;
        font-weight: 300;
        margin: rem(16) 0;
        font-size: rem(17);
        line-height: normal;
        color: $medium-grey-font;
      }
    }

    &__content {
      padding-top: rem(20);
      padding-left: rem(50);

      &-icon {
        width: rem(25);
        height: rem(25);
        float: left;
        font-size: rem(25);

        .p4a-icon {

          &:before {
            top: rem(-3);
            position: relative;
          }
        }
      }

      &-text {
        float: left;
        font-size: rem(13);
        max-width: rem(300);
        margin-left: rem(10);
      }

      &-title h2 {
        margin: 0;
        padding-top: 0;
        font-weight: 500;
        font-size: rem(13);
        line-height: rem(16);
        color: $dark-grey-font;
      }

      &-text-lines {
        max-height: rem(50);
        overflow: hidden;
        white-space: nowrap;
        padding-top: rem(7);
        line-height: rem(21);
        text-overflow: ellipsis;
        color: $medium-grey-font;
      }
    }

    &__footer {
      bottom: 0;
      width: 100%;
      position: absolute;
      padding-bottom: rem(25);

      &__button {
        float: right;
        margin-right: rem(25);

        &.btn--small {
          width: rem(95);
          height: rem(25);
          font-size: rem(13);
        }
      }

      &__cancel {

        .btn { color: $medium-grey-font; }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// node_modules/pro4all-ui/ui-kit/components/AlertModal.vue